import type { Tool, ToolGroupId } from '@/types/tools';

/**
 * Every tool in the portal, in one place. The Tools page, the Home page and
 * the tool cards all read this list, so a tool added here shows up everywhere.
 * A tool is grouped by the job a person came to do, not by how it is built.
 */
export const AVAILABLE_TOOLS: Tool[] = [
  // ── Set up a client ─────────────────────────────────────────────────────
  {
    id: 'setup-nextgen-client',
    name: 'Set up a NextGen client',
    description: 'Name the account, write the config, run the first deploy and get the system user password.',
    group: 'setup',
    route: '/tools/setup-nextgen-client',
    icon: 'rocket',
    category: 'management',
  },
  {
    id: 'setup-non-nextgen-client',
    name: 'Set up a non-NextGen client',
    description: "Check the account's access role, write the config and run the first deploy.",
    group: 'setup',
    route: '/tools/setup-non-nextgen-client',
    icon: 'rocket',
    category: 'management',
  },
  {
    id: 'create-client-config',
    name: 'Create client config',
    description: "Write a new client's config with the form or from a JSON file.",
    group: 'setup',
    route: '/tools/create-client-config',
    icon: 'file-plus',
    category: 'management',
  },

  // ── Change a client ─────────────────────────────────────────────────────
  {
    id: 'update-client-config',
    name: 'Update client config',
    description: "Change a client's region, apps or feature switches.",
    group: 'configs',
    route: '/tools/update-client-config',
    icon: 'pencil',
    category: 'management',
  },
  {
    id: 'bulk-update-client-config',
    name: 'Bulk update client config',
    description: 'Change one config field on many clients at once.',
    group: 'configs',
    route: '/tools/bulk-update-client-config',
    icon: 'layers',
    category: 'management',
  },
  {
    id: 'config-search',
    name: 'Config search',
    description: 'Find clients by keyword, or by the features they have switched on.',
    group: 'configs',
    route: '/tools/config-search',
    icon: 'search',
    category: 'analytics',
  },
  {
    id: 'delete-client-config',
    name: 'Delete client config',
    description: "Remove a client's config record. The AWS account is not touched.",
    group: 'configs',
    route: '/tools/delete-client-config',
    icon: 'trash',
    danger: true,
    category: 'management',
  },

  // ── Get into a client ───────────────────────────────────────────────────
  {
    id: 'get-system-user-secret',
    name: 'System user password',
    description: "Get the system user's password for a client account.",
    group: 'access',
    route: '/tools/get-system-user-secret',
    icon: 'key',
    category: 'management',
  },
  {
    id: 'reset-client-password',
    name: 'Reset a client password',
    description: "Set a new password for a person in a client's workspace, with no email step.",
    group: 'access',
    route: '/tools/reset-client-password',
    icon: 'shield-lock',
    category: 'management',
  },
  {
    id: 'reset-all-passwords',
    name: "Reset everyone's password",
    description: "Make every person in one client's workspace set a new password, and sign them out everywhere.",
    group: 'access',
    route: '/tools/reset-all-passwords',
    icon: 'shield-lock',
    category: 'management',
  },

  // ── Run the fleet ───────────────────────────────────────────────────────
  {
    id: 'announcements',
    name: 'Announcements',
    description:
      'Post a message to the people on chosen workspaces or deployment groups. Each person sees it once, as a pop-up.',
    group: 'fleet',
    route: '/tools/announcements',
    icon: 'megaphone',
    category: 'management',
  },
  {
    id: 'support-docs-manager',
    name: 'Support docs',
    description: 'Edit the master support documents and send them to client workspaces.',
    group: 'fleet',
    route: '/tools/support-docs-manager',
    icon: 'book',
    category: 'management',
  },
  {
    id: 'agent-library',
    name: 'Agent library',
    description: "Keep Arcanum's own agents and send them to client workspaces without a deploy.",
    group: 'fleet',
    route: '/tools/agent-library',
    icon: 'robot',
    category: 'management',
  },
  {
    id: 'fixed-ip-fleet',
    name: 'Fixed outgoing IP',
    description:
      "Every client with a fixed IP: whether calls to their servers leave from it, the last check, this month's charge. Pause and resume it.",
    group: 'fleet',
    route: '/tools/fixed-ip-fleet',
    icon: 'geo',
    category: 'management',
  },
  {
    id: 'platform-settings',
    name: 'Platform settings',
    description: 'Set the fleet-wide limits for automations: how many, how often, and when to warn.',
    group: 'fleet',
    route: '/tools/platform-settings',
    icon: 'sliders',
    category: 'management',
  },

  // ── Reports ─────────────────────────────────────────────────────────────
  {
    id: 'usage-report',
    name: 'Usage report',
    description: "Export a client's app runs, chat messages and user activity for a period.",
    group: 'reports',
    route: '/tools/usage-report',
    icon: 'bar-chart',
    category: 'analytics',
  },
  {
    id: 'cost-analytics',
    name: 'Cost analytics',
    description: "See a client's AWS cost by service and by month, and where it is heading.",
    group: 'reports',
    route: '/tools/cost-analytics',
    icon: 'currency',
    category: 'analytics',
  },
  {
    id: 'quota-report',
    name: 'Quota report',
    description: 'List the Bedrock request limits for each client account and region.',
    group: 'reports',
    route: '/tools/quota-report',
    icon: 'speedometer',
    category: 'analytics',
  },
  {
    id: 'all-users-report',
    name: 'All users report',
    description: 'List every user on one client or on all of them, with their role.',
    group: 'reports',
    route: '/tools/all-users-report',
    icon: 'people',
    category: 'analytics',
  },
];

export const TOOL_GROUPS: { id: ToolGroupId; name: string }[] = [
  { id: 'setup', name: 'Set up a client' },
  { id: 'configs', name: 'Change a client' },
  { id: 'access', name: 'Get into a client' },
  { id: 'fleet', name: 'Run the fleet' },
  { id: 'reports', name: 'Reports' },
];

/** The tools of each group, in the order the groups are shown. Empty groups are left out. */
export const getToolsByGroup = (tools: Tool[] = AVAILABLE_TOOLS) =>
  TOOL_GROUPS.map((group) => ({ ...group, tools: tools.filter((tool) => tool.group === group.id) })).filter(
    (group) => group.tools.length > 0
  );
